import { useTranslation } from 'next-i18next';

import { trackEvents } from '@boss/constants/shared';
import { useRouter } from '@boss/hooks';
import { ProductCard } from '@boss/ui';

import {
  useArticleAccountPrice,
  useContactPermissions,
  useEventTracker,
  useFavorites,
  usePrice,
  useProductAccountPrice,
  useProductMap,
} from '../../hooks';
import { isB2b } from '../../utils';

const useProductCardTranslations = (props: {
  translations?: React.ComponentProps<typeof ProductCard>['translations'];
  isArticle?: boolean;
  fromPriceUnit?: string;
}) => {
  const { t } = useTranslation('common');
  const { isArticle, fromPriceUnit } = props;

  return {
    quantityPrefix: !isArticle ? ((props.translations?.quantityPrefix ?? t('quantityPrefix')) as string) : '',
    disclaimer: !isArticle ? ((props.translations?.disclaimer ?? t('fromPriceDisclaimer')) as string) : '',
    unitPrice: fromPriceUnit ? t(`fromPriceUnits.${fromPriceUnit}`) ?? '' : '',
    unitLabel: fromPriceUnit && !isB2b && !isArticle ? t(`fromPriceUnitsShort.${fromPriceUnit}`) ?? '' : '',
  };
};

const isHeartHidden = (showprice: boolean, canorder: boolean): boolean => showprice === false && canorder === false;

const resolvePrice = (showPrice: boolean, showprice: boolean, price: number | undefined) =>
  showPrice && showprice !== false ? price : undefined;

const resolveStrikePrice = (showprice: boolean, strikePrice: number | undefined) =>
  showprice === false ? undefined : strikePrice;

const ProductCardWrapper = ({
  counterMin,
  price: productPrice,
  hideWishlistAction,
  hideAddToCartAction,
  showCounter,
  isArticle,
  unit,
  ...props
}: React.ComponentProps<typeof ProductCard> & {
  counterMin?: number;
  hideWishlistAction?: boolean;
  showCounter?: boolean;
  isArticle?: boolean;
  unit?: string;
}) => {
  const { favorites, toggleFavorite } = useFavorites();
  const { showPrice } = usePrice();
  const { trackCustomEvent } = useEventTracker();
  const { locale } = useRouter();
  const { showprice = true, canorder = true, loading = false } = useContactPermissions(locale) ?? {};
  const {
    priceToShow: productPriceToShow,
    fromPriceUnit: productFromPriceUnit,
    strikePrice: productStrikePrice,
    isLoading: priceInfoLoading,
    productLabels,
  } = useProductAccountPrice({ productId: props.id, price: productPrice });

  const {
    priceToShow: articlePriceToShow,
    strikePrice: articleStrikePrice,
    isLoading: articlePriceInfoLoading,
  } = useArticleAccountPrice({ articleId: props.id, price: productPrice, unit, enabled: !!isArticle });

  const price = isArticle ? articlePriceToShow : productPriceToShow;
  const strikePrice = isArticle ? articleStrikePrice : productStrikePrice;
  const fromPriceUnit = isArticle ? unit : productFromPriceUnit;

  const { mapProductLabels } = useProductMap();

  const labels =
    props.labels || productLabels ? [...mapProductLabels(productLabels || []), ...(props.labels || [])] : undefined;

  const translations = useProductCardTranslations({
    translations: props.translations,
    isArticle,
    fromPriceUnit,
  });

  const trackAddToWishlist = () => {
    const wishlistTrackingProps = {
      item_id: props.id,
      item_name: props.title,
      item_brand: 'boss-paints',
      item_category: props.trackInfo?.item_category || '',
      item_category2: props.trackInfo?.item_category2 || '',
      item_category3: props.trackInfo?.item_category3 || '',
      item_category4: props.trackInfo?.item_category4 || '',
      item_category5: props.trackInfo?.item_category5 || '',
      item_list_name: props.trackInfo?.item_list_name || 'product card',
      item_list_id: props.trackInfo?.item_list_id || '',
      price: price || 0,
      index: props.trackInfo?.index || 0,
    };

    trackCustomEvent(trackEvents.ADD_TO_WISHLIST, wishlistTrackingProps);
  };

  return (
    <ProductCard
      {...props}
      counterMin={counterMin}
      handleAddToWishlist={
        hideWishlistAction || isHeartHidden(showprice, canorder)
          ? undefined
          : () => {
              const isCurrentlyFavorite = favorites['PRODUCTS']?.includes(props.id);

              toggleFavorite('PRODUCTS', props.id);
              if (!isCurrentlyFavorite) {
                trackAddToWishlist();
              }
            }
      }
      hideAddToCartAction={hideAddToCartAction || canorder === false}
      inWishlist={favorites['PRODUCTS']?.includes(props.id)}
      labels={labels}
      // [39787] Rights not resolved yet: card shows skeletons in the gated slots
      // (price, heart, order) instead of painting content that may be revoked.
      permissionsLoading={loading}
      price={resolvePrice(showPrice, showprice, price)}
      priceIsLoading={priceInfoLoading || articlePriceInfoLoading || loading}
      showCounter={showCounter}
      strikePrice={resolveStrikePrice(showprice, isArticle ? articleStrikePrice : strikePrice)}
      translations={translations}
    />
  );
};

export default ProductCardWrapper;
